import React, { useEffect, useState } from 'react';
import { MdArrowBack, MdInventory, MdTrendingUp, MdTrendingDown, MdLocationOn, MdPhone, MdMail } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { fetchManufacturerDistributorDetail } from '../../../../services/Http/ManufacturerApi';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import StatsGrid from '../../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../../shared/layout/cards/StatCard';
import { useCurrency } from '../../../../context/CurrencyProvider';

const ManufacturerDistributorDetail = () => {
    const { distributorId } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const response = await fetchManufacturerDistributorDetail(distributorId);
                setData(response);
            } catch (err) {
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [distributorId]);

    if (loading) {
        return <LoadingMessage>{t('ManufacturerDistributorDetail.loading')}</LoadingMessage>;
    }

    if (error || !data) {
        return <ErrorMessage>{t('ManufacturerDistributorDetail.error')}</ErrorMessage>;
    }

    const { distributor, stats, venues, activity } = data;

    const statCards = [
        {
            key: 'inventory',
            value: `${(stats.inventory || 0).toLocaleString()} ${t('ManufacturerDistributorDetail.stats.units')}`,
            label: t('ManufacturerDistributorDetail.stats.inventory'),
            icon: <MdInventory className="icon" />,
            color: 'venues',
        },
        {
            key: 'sold',
            value: `${(stats.sold || 0).toLocaleString()} ${t('ManufacturerDistributorDetail.stats.units')}`,
            label: t('ManufacturerDistributorDetail.stats.sold'),
            icon: <MdTrendingUp className="icon" />,
            color: 'soldItems',
        },
        {
            key: 'inbound',
            value: `${formatResolvedAmount({
                eurAmount: stats.inbound_value,
                localAmount: stats.inbound_value_local ?? stats.inbound_local,
                localCurrencyId: stats.inbound_currency_id ?? stats.currency_id,
                localCurrencyCode: stats.inbound_currency_code ?? stats.currency_code,
            })} / ${(stats.inbound_quantity || 0).toLocaleString()} ${t('ManufacturerDistributorDetail.stats.units')}`,
            label: t('ManufacturerDistributorDetail.stats.inbound'),
            icon: <MdTrendingDown className="icon" />,
            color: 'transactions',
        },
        {
            key: 'outbound',
            value: `${formatResolvedAmount({
                eurAmount: stats.outbound_value,
                localAmount: stats.outbound_value_local ?? stats.outbound_local,
                localCurrencyId: stats.outbound_currency_id ?? stats.currency_id,
                localCurrencyCode: stats.outbound_currency_code ?? stats.currency_code,
            })} / ${(stats.outbound_quantity || 0).toLocaleString()} ${t('ManufacturerDistributorDetail.stats.units')}`,
            label: t('ManufacturerDistributorDetail.stats.outbound'),
            icon: <MdTrendingUp className="icon" />,
            color: 'revenue',
        },
        {
            key: 'venues',
            value: (stats.venues_count || 0).toLocaleString(),
            label: t('ManufacturerDistributorDetail.stats.venues'),
            icon: <MdLocationOn className="icon" />,
            color: 'users',
        },
    ];

    return (
        <PageContainer>
            <PageHeader>
                <BackButton onClick={() => navigate('/distributors')}>
                    <MdArrowBack /> {t('ManufacturerDistributorDetail.back')}
                </BackButton>
                <TitleBlock>
                    <PageTitle>{distributor.name}</PageTitle>
                    <Subtitle>{t('ManufacturerDistributorDetail.subtitle')}</Subtitle>
                </TitleBlock>
            </PageHeader>
            <PageContent>
                <StatsGrid>
                    {statCards.map((card) => (
                        <StatCard
                            key={card.key}
                            icon={card.icon}
                            title={card.label}
                            value={card.value}
                            color={card.color}
                        />
                    ))}
                </StatsGrid>

                <InfoGrid>
                    <InfoCard>
                        <SectionTitle>{t('ManufacturerDistributorDetail.contact.title')}</SectionTitle>
                        <ContactLine><MdMail /> {distributor.email || t('ManufacturerDistributorDetail.contact.notAvailable')}</ContactLine>
                        <ContactLine><MdPhone /> {distributor.phone || t('ManufacturerDistributorDetail.contact.notAvailable')}</ContactLine>
                        {distributor.website && (
                            <ContactLine><a href={distributor.website} target="_blank" rel="noreferrer">{distributor.website}</a></ContactLine>
                        )}
                        <ContactLine><MdLocationOn /> {distributor.address || t('ManufacturerDistributorDetail.contact.notAvailable')}</ContactLine>
                        {distributor.country && <ContactLine>{distributor.country}</ContactLine>}
                    </InfoCard>

                    <InfoCard>
                        <SectionTitle>{t('ManufacturerDistributorDetail.venues.title')}</SectionTitle>
                        {venues.length === 0 && <MutedText>{t('ManufacturerDistributorDetail.venues.empty')}</MutedText>}
                        {venues.length > 0 && (
                            <VenueList>
                                {venues.map((venue) => (
                                    <li key={venue.id}>
                                        <strong>{venue.name}</strong>
                                        <span>{venue.city || ''} {venue.country || ''}</span>
                                        <small>{venue.address}</small>
                                    </li>
                                ))}
                            </VenueList>
                        )}
                    </InfoCard>
                </InfoGrid>

                <TableSection>
                    <SectionTitle>{t('ManufacturerDistributorDetail.activity.title')}</SectionTitle>
                    {activity.length === 0 ? (
                        <MutedText>{t('ManufacturerDistributorDetail.activity.empty')}</MutedText>
                    ) : (
                        <StyledTable>
                            <thead>
                                <tr>
                                    <th>{t('ManufacturerDistributorDetail.activity.columns.date')}</th>
                                    <th>{t('ManufacturerDistributorDetail.activity.columns.direction')}</th>
                                    <th>{t('ManufacturerDistributorDetail.activity.columns.quantity')}</th>
                                    <th>{t('ManufacturerDistributorDetail.activity.columns.value')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activity.map((entry) => (
                                    <tr key={entry.id}>
                                        <td>{entry.date}</td>
                                        <td>{t(`ManufacturerDistributorDetail.activity.direction.${entry.direction}`)}</td>
                                        <td>{entry.quantity}</td>
                                        <td>{formatResolvedAmount({
                                            eurAmount: entry.value,
                                            localAmount: entry.value_local,
                                            localCurrencyId: entry.currency_id ?? stats.currency_id,
                                            localCurrencyCode: entry.currency_code ?? stats.currency_code,
                                        })}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </StyledTable>
                    )}
                </TableSection>
            </PageContent>
        </PageContainer>
    );
};

export default ManufacturerDistributorDetail;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    background: none;
    border: none;
    color: rgba(130, 22, 146, 1);
    font-weight: 600;
    cursor: pointer;
`;

const TitleBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const Subtitle = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.25rem;
    margin-top: 1.875rem;
`;

const InfoCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.2);
    border-radius: 12px;
    padding: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
`;

const SectionTitle = styled.h3`
    margin: 0;
    color: ${({ theme }) => theme.colors.textPrimary || '#333'};
`;

const ContactLine = styled.p`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};

    a {
        color: inherit;
        text-decoration: underline;
    }
`;

const MutedText = styled.p`
    margin: 0;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const VenueList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    li {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
    }

    strong {
        color: ${({ theme }) => theme.colors.textPrimary || '#333'};
    }
`;

const TableSection = styled.div`
    margin-top: 2.5rem;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        border-bottom: 1px solid rgba(130, 22, 146, 0.1);
        padding: 0.75rem;
        text-align: left;
    }

    th {
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
        font-weight: 600;
    }
`;
